import { useEffect, useState } from "react";
import {
  getTenantProfileData,
  updateTenantProfileData,
} from "../../../services/tenant/tenantServices";
import {
  getMasterIndustryList,
  getOccupationByIndustryId,
} from "../../../services/propertyOwner/propertyOwnerServices";
import { getMasterPincode } from "../../../services/admin/adminServices";
import toast from "../../../utils/toast";

export default function TenantProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [editLoading, setEditLoading] = useState(false);
  const [editErrors, setEditErrors] = useState({});
  const [industries, setIndustries] = useState([]);
  const [occupations, setOccupations] = useState([]);

  useEffect(() => {
    fetchProfile();
  }, []);

  useEffect(() => {
    if (isModalOpenEdit) {
      fetchIndustries();
    }
  }, [isModalOpenEdit]);

  useEffect(() => {
    if (editForm.industryId) fetchOccupations(Number(editForm.industryId));
    else setOccupations([]);
  }, [editForm.industryId]);

  const fetchProfile = async () => {
    try {
      const res = await getTenantProfileData();
      if (res.statusCode === 1) setProfile(res.data);
    } catch (err) {
      console.log("Failed to fetch profile");
    } finally {
      setLoading(false);
    }
  };

  const fetchIndustries = async () => {
    try {
      const res = await getMasterIndustryList();
      setIndustries(res.filter((i) => i.isActive));
    } catch (err) {
      console.log("Failed to fetch industries");
    }
  };

  const fetchOccupations = async (industryId) => {
    try {
      const res = await getOccupationByIndustryId(industryId);
      setOccupations(res.filter((o) => o.isActive));
    } catch (err) {
      console.log("Failed to fetch occupations");
    }
  };

  const openEditModal = () => {
    setEditForm({
      fullName: profile?.fullName || "",
      fatherName: profile?.fatherName || "",
      mobile: profile?.mobile || "",
      whatsApp: profile?.whatsApp || "",
      email: profile?.email || "",
      pincode: profile?.pincode || "",
      area: profile?.area || "",
      district: profile?.district || "",
      state: profile?.state || "",
      permanentAddress: profile?.permanentAddress || "",
      occupation: profile?.occupation || "",
      industryId: profile?.industryId || "",
      occupationId: profile?.occupationId || "",
      company: profile?.company || "",
    });
    setEditErrors({});
    setIsModalOpenEdit(true);
  };

  const closeEditModal = () => {
    setIsModalOpenEdit(false);
    setEditForm({});
    setEditErrors({});
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;

    if (name === "occupationId") {
      const selectedOcc = occupations.find(
        (o) => o.occupationId === Number(value),
      );
      setEditForm((prev) => ({
        ...prev,
        [name]: value,
        occupation: selectedOcc?.occupationName || "",
      }));
      setEditErrors((prev) => ({ ...prev, [name]: "" }));
      return;
    }

    setEditForm((prev) => ({ ...prev, [name]: value }));
    setEditErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const handlePincodeChange = async (e) => {
    handleEditChange(e);
    if (e.target.value.length === 6) {
      try {
        const res = await getMasterPincode(e.target.value);
        if (res.statusCode === 1) {
          setEditForm((prev) => ({
            ...prev,
            area: res.data.areaName || prev.area,
            district: res.data.district,
            state: res.data.stateName,
          }));
        }
      } catch (err) {}
    }
  };

  const validateEdit = () => {
    const e = {};
    if (!editForm.fullName) e.fullName = "Full name is required.";
    if (!editForm.mobile) e.mobile = "Mobile is required.";
    setEditErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleUpdateProfile = async () => {
    if (!validateEdit()) return;
    setEditLoading(true);
    try {
      const payload = {
        ...editForm,
        industryId: Number(editForm.industryId) || 0,
        occupationId: Number(editForm.occupationId) || 0,
      };
      const res = await updateTenantProfileData(payload);
      if (res.statusCode === 1) {
        toast.success(res.message || "Profile updated successfully.");
        closeEditModal();
        fetchProfile();
      } else {
        toast.error(res.message || "Failed to update profile.");
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const initials = profile?.fullName
    ?.split(" ")
    .map((w) => w[0])
    .join("")
    .toUpperCase();

  if (loading) {
    return (
      <div className="main">
        <div
          style={{
            padding: 40,
            textAlign: "center",
            color: "var(--text-tertiary)",
          }}
        >
          Loading profile...
        </div>
      </div>
    );
  }

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">My Profile</div>
            <div className="page-sub">Tenant › My Profile</div>
          </div>
          <div className="topbar-right">
            <button className="btn btn-primary" onClick={openEditModal}>
              ✎ Edit Profile
            </button>
          </div>
        </div>

        <div className="content">
          {/* Profile Header */}
          <div className="card" style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 20,
                flexWrap: "wrap",
              }}
            >
              <div
                style={{
                  width: 72,
                  height: 72,
                  borderRadius: "50%",
                  background: "rgb(250, 199, 117)",
                  color: "rgb(99, 56, 6)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: 26,
                  fontWeight: 700,
                  flexShrink: 0,
                  overflow: "hidden",
                }}
              >
                {profile?.photoUrl ? (
                  <img
                    src={profile.photoUrl}
                    alt="profile"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  initials || "T"
                )}
              </div>

              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 20, fontWeight: 700, marginBottom: 4 }}>
                  {profile?.fullName || "—"}
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    fontSize: 13,
                    color: "var(--text-secondary)",
                  }}
                >
                  <span>📧 {profile?.email || "—"}</span>
                  <span>📱 {profile?.mobile || "—"}</span>
                </div>
                <div
                  style={{
                    marginTop: 8,
                    display: "flex",
                    gap: 8,
                    flexWrap: "wrap",
                  }}
                >
                  <span className="badge badge-active">
                    Type : {profile?.tenantType || "Tenant"}
                  </span>
                  <span
                    className={`badge ${profile?.kycStatus === "verified" ? "badge-active" : "badge-warn"}`}
                  >
                    KYC : {profile?.kycStatus || "Pending"}
                  </span>
                  <span
                    style={{
                      fontSize: 11,
                      color: "var(--text-tertiary)",
                      alignSelf: "center",
                    }}
                  >
                    Member since: {formatDate(profile?.createdAt)}
                  </span>
                </div>
              </div>

              <div
                style={{
                  textAlign: "right",
                  fontSize: 12,
                  color: "var(--text-tertiary)",
                }}
              >
                <div>Tenancy Start</div>
                <div style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                  {formatDate(profile?.startDate)}
                </div>
                {profile?.endDate && (
                  <>
                    <div style={{ marginTop: 6 }}>Tenancy End</div>
                    <div
                      style={{ fontWeight: 600, color: "var(--text-primary)" }}
                    >
                      {formatDate(profile?.endDate)}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="two-col">
            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="card">
                <div className="card-title">Basic Information</div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "130px 1fr",
                    rowGap: 10,
                  }}
                >
                  {[
                    ["Full Name", profile?.fullName],
                    ["Father Name", profile?.fatherName],
                    ["Mobile", profile?.mobile],
                    ["WhatsApp", profile?.whatsApp],
                    ["Email", profile?.email],
                    ["Occupation", profile?.occupation],
                    ["Industry", profile?.industryName],
                    ["Company", profile?.company],
                  ].map(([label, value]) => (
                    <Row key={label} label={label} value={value} />
                  ))}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
              <div className="card">
                <div className="card-title">Address</div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "130px 1fr",
                    rowGap: 10,
                  }}
                >
                  {[
                    ["Area", profile?.area],
                    ["District", profile?.district],
                    ["State", profile?.state],
                    ["Pincode", profile?.pincode],
                    ["Permanent Address", profile?.permanentAddress],
                  ].map(([label, value]) => (
                    <Row key={label} label={label} value={value} />
                  ))}
                </div>
              </div>

              <div className="card">
                <div className="card-title">Tenancy Details</div>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "130px 1fr",
                    rowGap: 10,
                  }}
                >
                  {[
                    ["Tenant Type", profile?.tenantType],
                    ["KYC Status", profile?.kycStatusDisplay],
                    ["Start Date", formatDate(profile?.startDate)],
                    ["End Date", formatDate(profile?.endDate)],
                    ["Status", profile?.isActive ? "Active" : "Inactive"],
                  ].map(([label, value]) => (
                    <Row key={label} label={label} value={value} />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Edit Profile Modal ── */}
      {isModalOpenEdit && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 760, maxWidth: "98vw" }}>
            <div className="modal-header">
              <div className="modal-title">Edit Profile</div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>

            <div className="modal-body">
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "1fr 1fr",
                  gap: 20,
                }}
              >
                {/* LEFT */}
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--text-tertiary)",
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      marginBottom: 12,
                    }}
                  >
                    Personal Details
                  </div>

                  <div className="form-group mb12">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      name="fullName"
                      value={editForm.fullName}
                      onChange={handleEditChange}
                      placeholder="e.g. Priya Sharma"
                      style={
                        editErrors.fullName ? { borderColor: "#dc2626" } : {}
                      }
                    />
                    {editErrors.fullName && (
                      <span style={{ color: "#dc2626", fontSize: 11 }}>
                        {editErrors.fullName}
                      </span>
                    )}
                  </div>

                  <div className="form-group mb12">
                    <label>Father's Name</label>
                    <input
                      type="text"
                      name="fatherName"
                      value={editForm.fatherName}
                      onChange={handleEditChange}
                      placeholder="e.g. Ramesh Sharma"
                    />
                  </div>

                  <div className="form-grid mb12">
                    <div className="form-group">
                      <label>Mobile *</label>
                      <input
                        type="tel"
                        name="mobile"
                        value={editForm.mobile}
                        onChange={handleEditChange}
                        placeholder="10-digit number"
                        style={
                          editErrors.mobile ? { borderColor: "#dc2626" } : {}
                        }
                      />
                      {editErrors.mobile && (
                        <span style={{ color: "#dc2626", fontSize: 11 }}>
                          {editErrors.mobile}
                        </span>
                      )}
                    </div>
                    <div className="form-group">
                      <label>WhatsApp</label>
                      <input
                        type="tel"
                        name="whatsApp"
                        value={editForm.whatsApp}
                        onChange={handleEditChange}
                        placeholder="WhatsApp number"
                      />
                    </div>
                  </div>

                  <div className="form-group mb12">
                    <label>Email</label>
                    <input
                      type="email"
                      name="email"
                      value={editForm.email}
                      onChange={handleEditChange}
                      placeholder="email@example.com"
                    />
                  </div>

                  <div className="form-group mb12">
                    <label>Permanent Address</label>
                    <textarea
                      name="permanentAddress"
                      value={editForm.permanentAddress}
                      onChange={handleEditChange}
                      style={{ minHeight: 56 }}
                      placeholder="Full permanent address"
                    />
                  </div>

                  <div className="form-group mb12">
                    <label>Pincode</label>
                    <input
                      type="text"
                      name="pincode"
                      value={editForm.pincode}
                      onChange={handlePincodeChange}
                      maxLength={6}
                      placeholder="6-digit pincode"
                    />
                  </div>

                  <div className="form-grid mb12">
                    <div className="form-group">
                      <label>Area / Locality</label>
                      <input
                        type="text"
                        name="area"
                        value={editForm.area}
                        onChange={handleEditChange}
                        placeholder="Area"
                      />
                    </div>
                    <div className="form-group">
                      <label>District</label>
                      <input
                        type="text"
                        name="district"
                        value={editForm.district}
                        onChange={handleEditChange}
                        placeholder="District"
                      />
                    </div>
                  </div>

                  <div className="form-group mb12">
                    <label>State</label>
                    <input
                      type="text"
                      name="state"
                      value={editForm.state}
                      onChange={handleEditChange}
                      placeholder="State"
                    />
                  </div>
                </div>

                {/* RIGHT */}
                <div>
                  <div
                    style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: "var(--text-tertiary)",
                      textTransform: "uppercase",
                      letterSpacing: ".06em",
                      marginBottom: 12,
                    }}
                  >
                    Occupation Details
                  </div>

                  <div className="form-group mb12">
                    <label>Industry / Sector</label>
                    <select
                      name="industryId"
                      value={editForm.industryId}
                      onChange={handleEditChange}
                    >
                      <option value="">Select industry…</option>
                      {industries.map((ind) => (
                        <option key={ind.industryId} value={ind.industryId}>
                          {ind.icon} {ind.industryName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group mb12">
                    <label>Occupation / Role</label>
                    <select
                      name="occupationId"
                      value={editForm.occupationId}
                      onChange={handleEditChange}
                      disabled={!editForm.industryId}
                    >
                      <option value="">Select occupation…</option>
                      {occupations.map((occ) => (
                        <option key={occ.occupationId} value={occ.occupationId}>
                          {occ.occupationName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group mb12">
                    <label>Company / Organisation</label>
                    <input
                      type="text"
                      name="company"
                      value={editForm.company}
                      onChange={handleEditChange}
                      placeholder="Employer or institution name"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleUpdateProfile}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function Row({ label, value }) {
  return (
    <>
      <span
        style={{
          fontSize: 12,
          color: "var(--text-tertiary)",
          alignSelf: "start",
          paddingTop: 1,
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 13,
          color: value ? "var(--text-primary)" : "var(--text-tertiary)",
          fontWeight: value ? 500 : 400,
        }}
      >
        {value || "—"}
      </span>
    </>
  );
}
